import * as React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";

import { cn } from "utils/cn";
import { toggleFocusClasses } from "./Checkbox";
import { useFormFieldControl } from "./FormField";
import { useStableId } from "./useStableId";

const radioClasses = cn(
  "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-input bg-surface shadow-xs",
  "transition-colors duration-fast ease-standard",
  toggleFocusClasses,
  "disabled:cursor-not-allowed disabled:opacity-50",
  "data-[state=checked]:border-primary data-[state=checked]:bg-primary"
);

export interface RadioOption<V extends string = string> {
  value: V;
  label: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
}

export interface RadioGroupProps<V extends string = string>
  extends Omit<
    React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>,
    "value" | "defaultValue" | "onValueChange" | "children"
  > {
  options: RadioOption<V>[];
  /** "" selects nothing. */
  value?: V | "";
  defaultValue?: V;
  onValueChange?: (value: V) => void;
}

const RadioGroupInner = <V extends string = string>(
  { options, value, defaultValue, onValueChange, className, ...rest }: RadioGroupProps<V>,
  ref: React.ForwardedRef<HTMLDivElement>
) => {
  const props = useFormFieldControl(rest);
  const baseId = useStableId("radio", props.id);
  return (
    <RadioGroupPrimitive.Root
      ref={ref}
      data-ui="radio-group"
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange && ((next: string) => onValueChange(next as V))}
      className={cn("grid gap-3", className)}
      {...props}
    >
      {options.map((option, index) => {
        const itemId = `${baseId}-${index}`;
        const disabled = Boolean(props.disabled || option.disabled);
        return (
          <label
            key={option.value}
            htmlFor={itemId}
            className={cn(
              "flex items-start gap-3",
              disabled ? "cursor-not-allowed" : "cursor-pointer"
            )}
          >
            <RadioGroupPrimitive.Item
              id={itemId}
              value={option.value}
              disabled={option.disabled}
              aria-labelledby={`${itemId}-label`}
              aria-describedby={option.description ? `${itemId}-description` : undefined}
              className={radioClasses}
            >
              <RadioGroupPrimitive.Indicator className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />
            </RadioGroupPrimitive.Item>
            <span className={cn("flex min-w-0 flex-col gap-0.5", disabled && "opacity-60")}>
              <span
                id={`${itemId}-label`}
                className="text-sm font-medium leading-5 text-foreground"
              >
                {option.label}
              </span>
              {option.description && (
                <span
                  id={`${itemId}-description`}
                  className="text-sm leading-5 text-muted-foreground"
                >
                  {option.description}
                </span>
              )}
            </span>
          </label>
        );
      })}
    </RadioGroupPrimitive.Root>
  );
};

const RadioGroupWithRef = React.forwardRef(RadioGroupInner);
RadioGroupWithRef.displayName = "RadioGroup";

/** Vertical list of radio options with optional descriptions; works inside FormField. */
export const RadioGroup = RadioGroupWithRef as <V extends string = string>(
  props: RadioGroupProps<V> & { ref?: React.Ref<HTMLDivElement> }
) => React.ReactElement | null;
